import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useStore } from "react-redux";

import { productEventsApi } from "@/redux/productEvents.api";

import { createProductAnalytics } from "./client";

import type { AppDispatch, RootState } from "@/redux";
import type { ProductEventAction, ProductFeatureKey } from "@signalist/shared-types";

let activeClient: ReturnType<typeof createProductAnalytics> | undefined;
const track = (feature: ProductFeatureKey, action: ProductEventAction) =>
	activeClient?.track(feature, action);

/** Browser-only interactions. The Routing-owned client rechecks auth before every enqueue/send. */
export const useTrackFeature = () => useCallback(track, []);

/** One queue and timer per mounted Routing; no storage, user identifiers or beacon transport. */
export function useProductAnalytics(pathname: string) {
	const store = useStore<RootState>();
	const pathRef = useRef(pathname);
	const clientRef = useRef<ReturnType<typeof createProductAnalytics>>();

	useLayoutEffect(() => {
		pathRef.current = pathname;
	}, [pathname]);
	useLayoutEffect(() => {
		let pageHidden = false;
		const analytics = createProductAnalytics({
			getContext: () => {
				const { user, token } = store.getState().auth;

				return {
					session: user ? token : null,
					pathname: pathRef.current,
					visible: !pageHidden && document.visibilityState !== "hidden",
				};
			},
			send: (events) =>
				(store.dispatch as AppDispatch)(
					productEventsApi.endpoints.recordProductEvents.initiate(events, { track: false })
				).unwrap(),
		});

		clientRef.current = analytics;
		activeClient = analytics;
		const stop = analytics.start();
		const hide = () => {
			pageHidden = true;
			analytics.drop();
		};
		const show = () => {
			pageHidden = false;
		};
		const visibility = () => {
			if (document.visibilityState === "hidden") {
				analytics.drop();
			}
		};

		window.addEventListener("pagehide", hide);
		window.addEventListener("pageshow", show);
		document.addEventListener("visibilitychange", visibility);
		// Clear queues immediately on logout/account changes, even without a route render.
		const session = () => {
			const { user, token } = store.getState().auth;

			return user ? token : null;
		};
		let previousSession = session();
		const unsubscribe = store.subscribe(() => {
			const nextSession = session();

			if (nextSession !== previousSession) {
				previousSession = nextSession;
				analytics.trackRoute();
			}
		});

		analytics.trackRoute();

		return () => {
			stop();
			unsubscribe();
			activeClient = undefined;
			window.removeEventListener("pagehide", hide);
			window.removeEventListener("pageshow", show);
			document.removeEventListener("visibilitychange", visibility);
		};
	}, [store]);
	useEffect(() => {
		clientRef.current?.trackRoute();
	}, [pathname]);
}
